import { createContext, useContext, type ReactNode } from "react";
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { Navigate, useLocation } from "react-router-dom";

export const ME_QUERY = gql`
  query Me {
    me {
      authenticated
      isAdmin
      isGuardian
      role
      email
      locked
      canExitToOwner
      ownerName
      parentPinSet
      impersonationEnabled
      devTools
      features
      student {
        id
        name
        avatarUrl
      }
    }
  }
`;

export interface CurrentStudent {
  id: string;
  name: string;
  avatarUrl: string | null;
}

export interface AuthState {
  authenticated: boolean;
  isAdmin: boolean;
  /** Parent or teacher: every learner, their sessions and grading. */
  isGuardian: boolean;
  role: string | null;
  email: string | null;
  student: CurrentStudent | null;
  /** Kid mode: this device was handed over from a guardian account. */
  locked: boolean;
  /** Whether the kid shell should offer a PIN way back to a grown-up. */
  canExitToOwner: boolean;
  ownerName: string | null;
  parentPinSet: boolean;
  impersonationEnabled: boolean;
  /**
   * Whether the content-authoring workbench exists on this deployment at all —
   * the exercise browser, the theory page, the prompt layers (`DevTools` on the
   * server). A property of the instance rather than of the person: the same
   * predicate gates those resolvers, so a link drawn from this cannot offer a
   * page that will not open.
   */
  devTools: boolean;
  /**
   * Feature-flag keys open to this identity (see `Features` on the server).
   * The navigation draws from this, so a nav item and the page behind it cannot
   * disagree — but it is a courtesy to the reader, not a control: every gated
   * resolver checks the flag again.
   */
  features: string[];
  loading: boolean;
}

const EMPTY: AuthState = {
  authenticated: false,
  isAdmin: false,
  isGuardian: false,
  role: null,
  email: null,
  student: null,
  locked: false,
  canExitToOwner: false,
  parentPinSet: false,
  impersonationEnabled: false,
  devTools: false,
  features: [],
  ownerName: null,
  loading: true,
};

const AuthContext = createContext<AuthState>(EMPTY);

interface MeData {
  me: {
    authenticated: boolean;
    isAdmin: boolean;
    isGuardian: boolean;
    role: string | null;
    email: string | null;
    locked: boolean;
    canExitToOwner: boolean;
    ownerName: string | null;
    parentPinSet: boolean;
    impersonationEnabled: boolean;
    devTools: boolean;
    features: string[];
    student: CurrentStudent | null;
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { data, loading } = useQuery<MeData>(ME_QUERY, {
    fetchPolicy: "cache-and-network",
  });

  const me = data?.me;
  const value: AuthState = {
    authenticated: me?.authenticated ?? false,
    isAdmin: me?.isAdmin ?? false,
    isGuardian: me?.isGuardian ?? false,
    role: me?.role ?? null,
    email: me?.email ?? null,
    student: me?.student ?? null,
    locked: me?.locked ?? false,
    canExitToOwner: me?.canExitToOwner ?? false,
    ownerName: me?.ownerName ?? null,
    parentPinSet: me?.parentPinSet ?? false,
    devTools: me?.devTools ?? false,
    impersonationEnabled: me?.impersonationEnabled ?? false,
    features: me?.features ?? [],
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

function FullScreenSpinner() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary" />
    </div>
  );
}

/** Requires any authenticated user; otherwise redirects to the login page. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { authenticated, loading } = useAuth();
  const location = useLocation();

  // Only block once the first network result is in; the cache-and-network
  // policy keeps `loading` true on background refetches, so also allow through
  // once we already know the user is authenticated.
  if (loading && !authenticated) return <FullScreenSpinner />;
  if (!authenticated) return <Navigate to="/auth/login" replace state={{ from: location }} />;

  return <>{children}</>;
}

/**
 * Requires a guardian (parent/teacher). A learner is sent home — which, for a
 * learner, is the kid shell. The server enforces the same boundary; this only
 * keeps the UI from offering doors that would not open.
 */
export function RequireGuardian({ children }: { children: ReactNode }) {
  const { authenticated, isGuardian, loading } = useAuth();

  if (loading && !authenticated) return <FullScreenSpinner />;
  if (!authenticated) return <Navigate to="/auth/login" replace />;
  if (!isGuardian) return <Navigate to="/" replace />;

  return <>{children}</>;
}

/**
 * Requires the admin role: content authoring, prompts, AI spending.
 */
export function RequireAdmin({ children }: { children: ReactNode }) {
  const { authenticated, isAdmin, loading } = useAuth();

  if (loading && !authenticated) return <FullScreenSpinner />;
  if (!authenticated) return <Navigate to="/auth/login" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;

  return <>{children}</>;
}

/**
 * Requires the development-only content workbench (`DevTools` on the server).
 *
 * Admin **and** development, not either: these screens read and write files under
 * `content/`, which in production is a container's throwaway copy of what is in
 * git. Sent to `/admin` rather than to `/` — whoever typed this URL is an admin
 * looking for a tool, and the dashboard is where the tools that do exist are.
 */
export function RequireDevTools({ children }: { children: ReactNode }) {
  const { authenticated, isAdmin, devTools, loading } = useAuth();

  if (loading && !authenticated) return <FullScreenSpinner />;
  if (!authenticated) return <Navigate to="/auth/login" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;
  if (!devTools) return <Navigate to="/admin" replace />;

  return <>{children}</>;
}
